import { Injectable } from '@angular/core';

// In-browser model service for persona app (copies the earlier implementation)
type GenerationOptions = {
  max_new_tokens?: number;
  do_sample?: boolean;
  temperature?: number;
  top_p?: number;
  repetition_penalty?: number;
  no_repeat_ngram_size?: number;
  return_full_text?: boolean;
};

@Injectable({
  providedIn: 'root'
})
export class InBrowserModelService {
  private pipeline: any = null;
  private modelSlug: string;
  private loading: boolean = false;
  private usingWorkerProxy = false;

  private fallbackSlugs = [
    'Xenova/gpt-neo-125m',
    'Xenova/gpt2'
  ];

  public lastError: any = null;

  private runWithOnnxWarningFilter<T>(fn: () => Promise<T>): Promise<T> {
    const originalWarn = console.warn;
    const benignOnnxWarning = /\[W:onnxruntime:.*CleanUnusedInitializersAndNodeArgs/i;

    console.warn = (...args: any[]) => {
      try {
        const first = args && args.length > 0 ? String(args[0] ?? '') : '';
        if (benignOnnxWarning.test(first)) return;
      } catch (e) {}
      originalWarn.apply(console, args as any);
    };

    return fn().finally(() => {
      console.warn = originalWarn;
    });
  }

  constructor() {
    this.modelSlug = this.fallbackSlugs[0];
  }

  private configureRuntime(mod: any, useWorkerProxy: boolean, onProgress?: (msg: string) => void): void {
    try {
      const env = mod?.env;
      if (!env?.backends?.onnx?.wasm) return;
      env.backends.onnx.wasm.proxy = useWorkerProxy;
      // Keep CPU usage predictable while generation runs in the worker.
      env.backends.onnx.wasm.numThreads = 1;
      this.usingWorkerProxy = useWorkerProxy;
      if (onProgress) {
        onProgress(useWorkerProxy
          ? 'Runtime ONNX: worker proxy (UI non bloccata)'
          : 'Runtime ONNX: main thread (fallback)');
      }
    } catch (e) {
      console.warn('Failed to configure transformers runtime', e);
    }
  }

  isReady() {
    return this.pipeline !== null && !this.loading;
  }

  async init(onProgress?: (msg: string) => void) {
    if (this.pipeline) return this.pipeline;
    this.loading = true;
    if (onProgress) onProgress('Initializing in-browser model...');
    const mod = await import('@xenova/transformers');
    if (onProgress) onProgress('Loading pipeline...');
    
    // Setup IndexedDB caching for persistent model storage (survives page reloads)
    const cacheDir = new (window as any).URL('indexeddb://transformers-cache');
    
    // try primary slug then fallbacks. First with worker proxy, then main-thread fallback.
    const slugs = [this.modelSlug].concat(this.fallbackSlugs.filter(s => s !== this.modelSlug));
    let lastErr: any = null;
    const runtimeAttempts = [true, false];
    for (const useWorkerProxy of runtimeAttempts) {
      this.configureRuntime(mod, useWorkerProxy, onProgress);
      for (const slug of slugs) {
        try {
          if (onProgress) {
            onProgress(`Trying model (${useWorkerProxy ? 'worker' : 'fallback'}): ` + slug);
          }
          this.pipeline = await this.runWithOnnxWarningFilter(() =>
            mod.pipeline('text-generation', slug, {
              cache_dir: cacheDir
            })
          );
          this.modelSlug = slug;
          if (onProgress) {
            onProgress(`Model cached: ${slug} (${this.usingWorkerProxy ? 'worker' : 'fallback'})`);
          }
          break;
        } catch (e) {
          lastErr = e;
          console.warn('Model init failed for', slug, useWorkerProxy ? '[worker]' : '[fallback]', e);
        }
      }
      if (this.pipeline) break;
    }
    if (!this.pipeline) {
      this.lastError = lastErr;
      this.loading = false;
      throw lastErr || new Error('Failed to load any in-browser model');
    }
    this.loading = false;
    if (onProgress) onProgress('Model ready');
    return this.pipeline;
  }

  async generate(prompt: string, opts: GenerationOptions = {}) {
    if (!this.pipeline) await this.init();
    const params = {
      max_new_tokens: opts.max_new_tokens ?? 64,
      do_sample: opts.do_sample ?? true,
      temperature: opts.temperature ?? 0.72,
      top_p: opts.top_p ?? 0.9,
      repetition_penalty: opts.repetition_penalty ?? 1.12,
      no_repeat_ngram_size: opts.no_repeat_ngram_size ?? 3,
      return_full_text: opts.return_full_text ?? false
    };
    console.log('[MODEL] Generating with params:', params, 'prompt length:', prompt.length);
    const generateStart = Date.now();
    const out = await this.pipeline(prompt, params);
    console.log(`[MODEL] Raw pipeline output (elapsed ${Date.now() - generateStart}ms):`, out);
    
    if (Array.isArray(out)) {
      const result = out[0].generated_text ?? out[0].text ?? JSON.stringify(out);
      console.log('[MODEL] Extracted from array:', result?.substring?.(0, 200));
      return result;
    }
    const result = out.generated_text ?? out.text ?? JSON.stringify(out);
    console.log('[MODEL] Direct result:', result?.substring?.(0, 200));
    return result;
  }

  async dispose() {
    try {
      if (this.pipeline && typeof this.pipeline.dispose === 'function') {
        await this.pipeline.dispose();
      }
    } catch (e) {}
    this.pipeline = null;
  }
}
